import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { BankDocumentEntryModel } from '../Model/bankDocument.model';
import type {
  BankDocumentAllocationTargetType,
  BankDocumentEntryAllocationInputDto,
} from '../Types/bankDocument.types';
import {
  BankDocumentApContractorLookup,
  BankDocumentBookingLookup,
  BankDocumentExpenseInvoiceLookup,
  BankDocumentInvoiceLookup,
  BankDocumentReservationLookup,
  useGetBankDocumentBookingsLookupQuery,
  useCreateSupplierPrepaymentApContractorMutation,
  useGetBankDocumentApContractorsLookupQuery,
  useGetBankDocumentCompaniesLookupQuery,
  useGetBankDocumentExpenseInvoicesLookupQuery,
  useGetBankDocumentInvoicesLookupQuery,
  useGetBankDocumentReservationsLookupQuery,
} from '../Service';
import { isBankDocumentsReservationQuickCreateEnabled } from '../Utils/bankDocumentsFeatureFlags';
import { normalizeMoneyToEven2dp } from '../Service/bankDocumentsRequestBuilder';

type AssignmentType = 'invoice' | 'reservation' | 'expenseInvoice' | 'supplierPrepayment' | 'none';

export interface AssignEntryDialogResult {
  assignmentType: AssignmentType;
  invoiceId?: number | null;
  reservationId?: number | null;
  expenseInvoiceId?: number | null;
  supplierApContractorId?: number | null;
  allocations?: BankDocumentEntryAllocationInputDto[] | null;
}

interface AssignEntryDialogProps {
  open: boolean;
  entry: BankDocumentEntryModel | null;
  saving: boolean;
  errorMessage?: string | null;
  companyId?: number | null;
  draftReservationId?: number;
  onStartReservationQuickCreateFromBooking?: (bookingId: number) => void;
  onStartReservationQuickCreateWithNewBooking?: () => void;
  onClose: () => void;
  onSave: (result: AssignEntryDialogResult) => void;
}

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('pl-PL');
  } catch {
    return value;
  }
};

const normalizeNip = (value: string): string => value.replace(/[^0-9]/g, '');
const roundAmount = (value: number): number => normalizeMoneyToEven2dp(value);
const normalizeSecondarySegment = (value?: string | null): string | null => {
  const normalized = value?.trim();
  return normalized ? normalized : null;
};
const buildSecondaryLine = (...segments: Array<string | null | undefined>): string =>
  segments
    .map((segment) => normalizeSecondarySegment(segment))
    .filter((segment): segment is string => Boolean(segment))
    .join(' • ');
const normalizeDisplayLabel = (value?: string | null): string | null => {
  const normalized = normalizeSecondarySegment(value);
  return normalized ? normalized.replace(/\s+/g, ' ') : null;
};
const areDisplayLabelsEqual = (left?: string | null, right?: string | null): boolean => {
  const normalizedLeft = normalizeDisplayLabel(left)?.toLocaleLowerCase('pl-PL');
  const normalizedRight = normalizeDisplayLabel(right)?.toLocaleLowerCase('pl-PL');
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
};

interface AllocationDraftRow {
  localId: string;
  targetType: BankDocumentAllocationTargetType;
  targetId: number | null;
  applicantName?: string;
  allocatedAmount: number | null;
}

const pluralizeItems = (count: number): string => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  const isFew = lastDigit >= 2 && lastDigit <= 4 && !(lastTwoDigits >= 12 && lastTwoDigits <= 14);
  if (count === 1) return '1 pozycja';
  return `${count} ${isFew ? 'pozycje' : 'pozycji'}`;
};

const formatAllocationTarget = (allocation: AllocationDraftRow): string =>
  `${allocation.targetType === 'invoice' ? 'Faktura' : 'Rezerwacja'} #${allocation.targetId}`;

// Applicant name is known for existing reservation allocations only; blank values are treated as missing.
const resolveExistingApplicantName = (allocation: {
  targetType: BankDocumentAllocationTargetType;
  reservationApplicantName?: string | null;
}): string | undefined => {
  if (allocation.targetType !== 'reservation') return undefined;
  return allocation.reservationApplicantName?.trim() || undefined;
};

const AssignEntryDialog: React.FC<AssignEntryDialogProps> = ({
  open,
  entry,
  saving,
  errorMessage,
  companyId,
  draftReservationId,
  onStartReservationQuickCreateFromBooking,
  onStartReservationQuickCreateWithNewBooking,
  onClose,
  onSave,
}) => {
  const [assignmentType, setAssignmentType] = useState<AssignmentType>('none');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [lookupPageNumber, setLookupPageNumber] = useState(1);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [selectedReservationId, setSelectedReservationId] = useState<number | null>(null);
  const [selectedExpenseInvoiceId, setSelectedExpenseInvoiceId] = useState<number | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [debouncedBookingSearchTerm, setDebouncedBookingSearchTerm] = useState('');
  const [bookingLookupPageNumber, setBookingLookupPageNumber] = useState(1);
  const [isBookingLookupVisible, setIsBookingLookupVisible] = useState(false);
  const [allocationRows, setAllocationRows] = useState<AllocationDraftRow[]>([]);
  const [selectedSupplierApContractor, setSelectedSupplierApContractor] =
    useState<BankDocumentApContractorLookup | null>(null);
  const [isCreateApContractorOpen, setIsCreateApContractorOpen] = useState(false);
  const [newApContractorName, setNewApContractorName] = useState('');
  const [newApContractorNip, setNewApContractorNip] = useState('');
  const [createApContractorError, setCreateApContractorError] = useState<string | null>(null);
  const [createApContractorSuccess, setCreateApContractorSuccess] = useState<string | null>(null);
  const normalizedSearchTerm = debouncedSearchTerm.trim();
  const normalizedBookingSearchTerm = debouncedBookingSearchTerm.trim();
  const normalizedSupplierApContractorInput = searchTerm.trim();
  const hasSupplierApContractorSearchTerm = normalizedSupplierApContractorInput.length > 0;
  const hasMinSupplierApContractorSearchLength = normalizedSupplierApContractorInput.length >= 2;
  const shouldRunSupplierApContractorLookup =
    open && assignmentType === 'supplierPrepayment' && normalizedSearchTerm.length >= 2;
  const isSupplierApContractorDebouncePending =
    hasMinSupplierApContractorSearchLength &&
    normalizedSupplierApContractorInput !== normalizedSearchTerm;
  const hasSearchTerm = normalizedSearchTerm.length > 0;
  const hasMinSearchLength = normalizedSearchTerm.length >= 2;
  const shouldSkipLookupBySearchLength = !hasMinSearchLength;
  const hasBookingSearchTerm = normalizedBookingSearchTerm.length > 0;
  const hasMinBookingSearchLength = normalizedBookingSearchTerm.length >= 2;
  const shouldSkipBookingLookupBySearchLength = !hasMinBookingSearchLength;

  const { companies } = useGetBankDocumentCompaniesLookupQuery();
  const {
    apContractors,
    isLoading: apContractorsLoading,
    isFetching: apContractorsFetching,
  } = useGetBankDocumentApContractorsLookupQuery(
    { searchTerm: normalizedSearchTerm },
    { skip: !shouldRunSupplierApContractorLookup }
  );
  const [createApContractor, { isLoading: isCreateApContractorLoading }] =
    useCreateSupplierPrepaymentApContractorMutation();
  const selectedCompany = companyId ? companies.find((company) => company.id === companyId) : null;
  const isOutflow = entry ? entry.amount < 0 : false;
  const hasReservationOnlyAllocations =
    (entry?.allocationCount ?? 0) === 0 || entry?.allocationTypeSummary === 'reservation';
  const isReservationQuickCreateEntryEligible =
    !isOutflow &&
    Boolean(entry) &&
    Boolean(entry && entry.remainingAmount > 0) &&
    hasReservationOnlyAllocations;
  const reservationQuickCreateDisabledReason = !entry
    ? null
    : entry.remainingAmount <= 0
      ? 'Szybkie tworzenie rezerwacji jest dostępne tylko dla pozycji z dodatnią pozostałą kwotą.'
      : !hasReservationOnlyAllocations
        ? 'Szybkie tworzenie jest zablokowane dla pozycji z alokacjami spoza rodziny rezerwacji.'
        : null;
  // Refunds are split across reservations only, as positive magnitudes (|Amount|).
  const entryAmountAbs = entry ? Math.abs(entry.amount) : 0;
  const isAllocationMode = isOutflow
    ? assignmentType === 'reservation'
    : assignmentType === 'invoice' || assignmentType === 'reservation';
  const entryAmountNoun = isOutflow ? 'zwrotu' : 'wpłaty';
  const shouldShowReservationQuickCreateCta =
    isBankDocumentsReservationQuickCreateEnabled && assignmentType === 'reservation' && !isOutflow;

  useEffect(() => {
    if (!open || !entry) return;

    const existingAllocationRows =
      entry.allocations?.map((allocation) => ({
        localId: `${allocation.targetType}-${allocation.targetId}`,
        targetType: allocation.targetType,
        targetId: allocation.targetId,
        applicantName: resolveExistingApplicantName(allocation),
        allocatedAmount: Math.abs(allocation.allocatedAmount),
      })) ?? [];

    const canHydrateAllocations =
      existingAllocationRows.length > 0 &&
      (entry.amount > 0 || existingAllocationRows.every((row) => row.targetType === 'reservation'));

    if (canHydrateAllocations) {
      setAssignmentType(existingAllocationRows[0].targetType);
      setAllocationRows(existingAllocationRows);
      setSelectedInvoiceId(null);
      setSelectedReservationId(null);
      setSelectedExpenseInvoiceId(null);
      setSelectedSupplierApContractor(null);
    } else if (entry.amount > 0 && entry.invoiceId) {
      setAssignmentType('invoice');
      setAllocationRows([
        {
          localId: `invoice-${entry.invoiceId}`,
          targetType: 'invoice',
          targetId: entry.invoiceId,
          allocatedAmount: entry.amount,
        },
      ]);
      setSelectedInvoiceId(null);
      setSelectedReservationId(null);
      setSelectedExpenseInvoiceId(null);
      setSelectedSupplierApContractor(null);
    } else if (entry.amount !== 0 && entry.reservationId && (entry.amount > 0 || !entry.invoiceId)) {
      setAssignmentType('reservation');
      setAllocationRows([
        {
          localId: `reservation-${entry.reservationId}`,
          targetType: 'reservation',
          targetId: entry.reservationId,
          allocatedAmount: Math.abs(entry.amount),
        },
      ]);
      setSelectedInvoiceId(null);
      setSelectedReservationId(null);
      setSelectedExpenseInvoiceId(null);
      setSelectedSupplierApContractor(null);
    } else if (entry.invoiceId) {
      setAssignmentType('invoice');
      setSelectedInvoiceId(entry.invoiceId);
      setSelectedReservationId(null);
      setSelectedExpenseInvoiceId(null);
      setAllocationRows([]);
      setSelectedSupplierApContractor(null);
    } else if (entry.reservationId) {
      setAssignmentType('reservation');
      setSelectedReservationId(entry.reservationId);
      setSelectedInvoiceId(null);
      setSelectedExpenseInvoiceId(null);
      setAllocationRows([]);
      setSelectedSupplierApContractor(null);
    } else if (entry.expenseInvoiceId) {
      setAssignmentType('expenseInvoice');
      setSelectedExpenseInvoiceId(entry.expenseInvoiceId);
      setSelectedInvoiceId(null);
      setSelectedReservationId(null);
      setAllocationRows([]);
      setSelectedSupplierApContractor(null);
    } else {
      setAssignmentType(draftReservationId != null && entry.remainingAmount > 0 ? 'reservation' : 'none');
      setSelectedInvoiceId(null);
      setSelectedReservationId(draftReservationId != null && entry.remainingAmount > 0 ? draftReservationId : null);
      setSelectedExpenseInvoiceId(null);
      setAllocationRows(
        draftReservationId != null && entry.remainingAmount > 0
          ? [
              {
                localId: `reservation-${draftReservationId}`,
                targetType: 'reservation',
                targetId: draftReservationId,
                allocatedAmount: entry.remainingAmount,
              },
            ]
          : []
      );
      setSelectedSupplierApContractor(null);
    }

    setSearchTerm('');
    setDebouncedSearchTerm('');
    setLookupPageNumber(1);
    setBookingSearchTerm('');
    setDebouncedBookingSearchTerm('');
    setBookingLookupPageNumber(1);
    setSelectedBookingId(null);
    setIsBookingLookupVisible(false);
    setIsCreateApContractorOpen(false);
    setNewApContractorName('');
    setNewApContractorNip('');
    setCreateApContractorError(null);
    setCreateApContractorSuccess(null);
  }, [open, entry, draftReservationId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBookingSearchTerm(bookingSearchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [bookingSearchTerm]);

  const invoicesLookupQuery = useGetBankDocumentInvoicesLookupQuery(
    { searchTerm: normalizedSearchTerm, pageNumber: lookupPageNumber, pageSize: 12, companyId },
    { skip: !open || assignmentType !== 'invoice' || shouldSkipLookupBySearchLength }
  );

  const invoices = invoicesLookupQuery.data ?? [];
  const invoicesFetching =
    'isFetching' in invoicesLookupQuery ? Boolean(invoicesLookupQuery.isFetching) : false;

  const reservationsLookupQuery = useGetBankDocumentReservationsLookupQuery(
    { searchTerm: normalizedSearchTerm, pageNumber: lookupPageNumber, pageSize: 12, companyId },
    { skip: !open || assignmentType !== 'reservation' || shouldSkipLookupBySearchLength }
  );

  const reservations = reservationsLookupQuery.data ?? [];
  const reservationsFetching =
    'isFetching' in reservationsLookupQuery ? Boolean(reservationsLookupQuery.isFetching) : false;

  const bookingsLookupQuery = useGetBankDocumentBookingsLookupQuery(
    {
      searchTerm: normalizedBookingSearchTerm,
      pageNumber: bookingLookupPageNumber,
      pageSize: 10,
      companyId,
    },
    {
      skip:
        !open ||
        !shouldShowReservationQuickCreateCta ||
        !isBookingLookupVisible ||
        shouldSkipBookingLookupBySearchLength,
    }
  );

  const bookings = bookingsLookupQuery.data ?? [];
  const bookingsFetching =
    'isFetching' in bookingsLookupQuery ? Boolean(bookingsLookupQuery.isFetching) : false;

  const expenseInvoicesLookupQuery = useGetBankDocumentExpenseInvoicesLookupQuery(
    { searchTerm: normalizedSearchTerm, pageNumber: lookupPageNumber, pageSize: 12, companyId },
    { skip: !open || assignmentType !== 'expenseInvoice' || shouldSkipLookupBySearchLength }
  );

  const expenseInvoices = expenseInvoicesLookupQuery.data ?? [];
  const expenseInvoicesFetching =
    'isFetching' in expenseInvoicesLookupQuery
      ? Boolean(expenseInvoicesLookupQuery.isFetching)
      : false;

  const handleAssignmentTypeChange = (value: AssignmentType) => {
    setAssignmentType(value);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setLookupPageNumber(1);
    setBookingSearchTerm('');
    setDebouncedBookingSearchTerm('');
    setBookingLookupPageNumber(1);
    setSelectedBookingId(null);
    setIsBookingLookupVisible(false);
    setSelectedInvoiceId(null);
    setSelectedReservationId(null);
    setSelectedExpenseInvoiceId(null);
    setAllocationRows([]);
    setSelectedSupplierApContractor(null);
    setIsCreateApContractorOpen(false);
    setNewApContractorName('');
    setNewApContractorNip('');
    setCreateApContractorError(null);
    setCreateApContractorSuccess(null);
  };

  const handleCreateApContractor = async () => {
    setCreateApContractorError(null);
    setCreateApContractorSuccess(null);

    const trimmedName = newApContractorName.trim();
    const normalizedNip = normalizeNip(newApContractorNip);

    if (!trimmedName) {
      setCreateApContractorError('Nazwa AP kontrahenta jest wymagana.');
      return;
    }

    if (normalizedNip.length !== 10) {
      setCreateApContractorError('NIP musi zawierać dokładnie 10 cyfr.');
      return;
    }

    try {
      const created = await createApContractor({ name: trimmedName, nip: normalizedNip }).unwrap();
      setSelectedSupplierApContractor({
        id: created.id,
        name: created.name,
        nip: created.nip,
      });
      setSearchTerm(renderApContractorRowLabel(created));
      setIsCreateApContractorOpen(false);
      setNewApContractorName('');
      setNewApContractorNip('');
      setCreateApContractorSuccess(`Dodano AP kontrahenta: ${created.name}.`);
    } catch {
      setCreateApContractorError('Nie udało się utworzyć AP kontrahenta.');
    }
  };

  const getRoundedAllocationAmount = (allocatedAmount?: number | null) =>
    roundAmount(allocatedAmount ?? 0);

  const allocationAssignedAmount = useMemo(
    () =>
      roundAmount(
        allocationRows.reduce(
          (sum, allocation) => sum + getRoundedAllocationAmount(allocation.allocatedAmount),
          0
        )
      ),
    [allocationRows]
  );
  const allocationRemainingAmount = entry
    ? roundAmount(Math.max(0, entryAmountAbs - allocationAssignedAmount))
    : 0;
  const hasMixedAllocationTypes = useMemo(
    () =>
      allocationRows.length > 0 &&
      allocationRows.some((allocation) => allocation.targetType !== allocationRows[0].targetType),
    [allocationRows]
  );
  const allocationValidationMessage = useMemo(() => {
    if (!isAllocationMode) {
      return null;
    }

    if (allocationRows.length === 0) {
      return null;
    }

    if (hasMixedAllocationTypes) {
      return `Nie można mieszać faktur i rezerwacji w jednej ${isOutflow ? 'pozycji zwrotu' : 'wpłacie'}.`;
    }

      if (
        allocationRows.some(
          (allocation) =>
            !allocation.targetId || getRoundedAllocationAmount(allocation.allocatedAmount) <= 0
        )
      ) {
        return 'Każda alokacja musi mieć wybrany cel i kwotę większą od zera.';
      }

    if (entry && allocationAssignedAmount > entryAmountAbs) {
      return `Suma alokacji przekracza kwotę ${entryAmountNoun}.`;
    }

    return null;
  }, [
    allocationAssignedAmount,
    allocationRows,
    entry,
    entryAmountAbs,
    entryAmountNoun,
    hasMixedAllocationTypes,
    isAllocationMode,
    isOutflow,
  ]);

  const resolveNextAllocationAmount = (
    remainingEntryAmount: number,
    targetRemainingAmount?: number | null,
    allowTargetOverallocation = false
  ): number | null => {
    if (remainingEntryAmount <= 0) {
      return null;
    }

    if (targetRemainingAmount == null) {
      return remainingEntryAmount;
    }

    const cappedTargetRemaining = roundAmount(Math.max(0, targetRemainingAmount));
    if (cappedTargetRemaining <= 0) {
      return allowTargetOverallocation ? remainingEntryAmount : null;
    }

    return roundAmount(Math.min(remainingEntryAmount, cappedTargetRemaining));
  };

  const getNextAllocationAmount = (
    targetRemainingAmount?: number | null,
    allowTargetOverallocation = false
  ): number | null => {
    if (!entry) {
      return null;
    }

    const remainingEntryAmount = roundAmount(Math.max(0, entryAmountAbs - allocationAssignedAmount));
    return resolveNextAllocationAmount(
      remainingEntryAmount,
      targetRemainingAmount,
      allowTargetOverallocation
    );
  };

  const addAllocationRow = (
    targetType: BankDocumentAllocationTargetType,
    targetId: number,
    targetRemainingAmount?: number | null,
    allowTargetOverallocation = false,
    applicantName?: string
  ) => {
    setAllocationRows((current) => {
      if (current.some((allocation) => allocation.targetType === targetType && allocation.targetId === targetId)) {
        return current;
      }

      const assignedAmount = roundAmount(
        current.reduce(
          (sum, allocation) => sum + getRoundedAllocationAmount(allocation.allocatedAmount),
            0
        )
      );
      const remainingEntryAmount = entry ? roundAmount(Math.max(0, entryAmountAbs - assignedAmount)) : 0;
      const nextAllocationAmount = resolveNextAllocationAmount(
        remainingEntryAmount,
        targetRemainingAmount,
        allowTargetOverallocation
      );
      if (nextAllocationAmount === null) {
        return current;
      }

      return [
        ...current,
        {
          localId: `${targetType}-${targetId}`,
          targetType,
          targetId,
          applicantName: applicantName?.trim() || undefined,
          allocatedAmount: nextAllocationAmount,
        },
      ];
    });
  };

  const updateAllocationAmount = (localId: string, allocatedAmount: number | null) => {
    setAllocationRows((current) =>
      current.map((allocation) =>
        allocation.localId === localId
          ? {
              ...allocation,
              allocatedAmount,
            }
          : allocation
      )
    );
  };

  const removeAllocationRow = (localId: string) => {
    setAllocationRows((current) => current.filter((allocation) => allocation.localId !== localId));
  };

  const handleSave = () => {
    if (isAllocationMode) {
      if (allocationValidationMessage) {
        return;
      }

      const allocations = allocationRows.flatMap((allocation) =>
        allocation.targetId != null && allocation.allocatedAmount != null
          ? [
              {
                targetType: allocation.targetType,
                targetId: allocation.targetId,
                allocatedAmount: allocation.allocatedAmount,
              },
            ]
          : []
      );

      if (allocations.length === 0) {
        onSave({
          assignmentType: 'none',
          invoiceId: null,
          reservationId: null,
          expenseInvoiceId: null,
          supplierApContractorId: null,
          allocations: null,
        });
        return;
      }

      onSave({
        assignmentType,
        invoiceId: null,
        reservationId: null,
        expenseInvoiceId: null,
        supplierApContractorId: null,
        allocations,
      });
      return;
    } else if (assignmentType === 'invoice') {
      onSave({
        assignmentType,
        invoiceId: selectedInvoiceId,
        reservationId: null,
        expenseInvoiceId: null,
        supplierApContractorId: null,
        allocations: null,
      });
    } else if (assignmentType === 'reservation') {
      onSave({
        assignmentType,
        invoiceId: null,
        reservationId: selectedReservationId,
        expenseInvoiceId: null,
        supplierApContractorId: null,
        allocations: null,
      });
    } else if (assignmentType === 'expenseInvoice') {
      onSave({
        assignmentType,
        invoiceId: null,
        reservationId: null,
        expenseInvoiceId: selectedExpenseInvoiceId,
        supplierApContractorId: null,
        allocations: null,
      });
    } else if (assignmentType === 'supplierPrepayment') {
      onSave({
        assignmentType,
        invoiceId: null,
        reservationId: null,
        expenseInvoiceId: null,
        supplierApContractorId: selectedSupplierApContractor?.id ?? null,
        allocations: null,
      });
    } else {
      onSave({
        assignmentType,
        invoiceId: null,
        reservationId: null,
        expenseInvoiceId: null,
        supplierApContractorId: null,
        allocations: null,
      });
    }
  };

  const isSaveDisabled =
    saving ||
    (isAllocationMode && Boolean(allocationValidationMessage)) ||
    (!isAllocationMode && assignmentType === 'invoice' && !selectedInvoiceId) ||
    (!isAllocationMode && assignmentType === 'reservation' && !selectedReservationId) ||
    (assignmentType === 'expenseInvoice' && !selectedExpenseInvoiceId) ||
    (assignmentType === 'supplierPrepayment' && !selectedSupplierApContractor) ||
    isCreateApContractorLoading;

  const activeLookupPagination =
    assignmentType === 'invoice'
      ? invoicesLookupQuery.pagination
      : assignmentType === 'reservation'
        ? reservationsLookupQuery.pagination
        : assignmentType === 'expenseInvoice'
          ? expenseInvoicesLookupQuery.pagination
          : null;

  const activeLookupIsFetching =
    assignmentType === 'invoice'
      ? invoicesFetching
      : assignmentType === 'reservation'
        ? reservationsFetching
        : assignmentType === 'expenseInvoice'
          ? expenseInvoicesFetching
          : false;
  const supportsLookupPagination =
    assignmentType === 'invoice' ||
    assignmentType === 'reservation' ||
    assignmentType === 'expenseInvoice';

  const canGoToPreviousLookupPage = supportsLookupPagination && lookupPageNumber > 1;
  const canGoToNextLookupPage =
    supportsLookupPagination &&
    !!activeLookupPagination &&
    lookupPageNumber * activeLookupPagination.pageSize < activeLookupPagination.totalCount;

  const clearActiveLookupSelection = () => {
    if (assignmentType === 'invoice') {
      setSelectedInvoiceId(null);
    } else if (assignmentType === 'reservation') {
      setSelectedReservationId(null);
    } else if (assignmentType === 'expenseInvoice') {
      setSelectedExpenseInvoiceId(null);
    }
  };

  const handleLookupPageChange = (getNextPageNumber: (currentPageNumber: number) => number) => {
    clearActiveLookupSelection();
    setLookupPageNumber((currentPageNumber) => getNextPageNumber(currentPageNumber));
  };

  const showLookupPaginationControls =
    supportsLookupPagination &&
    hasMinSearchLength &&
    !!activeLookupPagination &&
    activeLookupPagination.totalCount > activeLookupPagination.pageSize;
  const canGoToPreviousBookingLookupPage = bookingLookupPageNumber > 1;
  const canGoToNextBookingLookupPage =
    !!bookingsLookupQuery.pagination &&
    bookingLookupPageNumber * bookingsLookupQuery.pagination.pageSize <
      bookingsLookupQuery.pagination.totalCount;
  const showBookingLookupPaginationControls =
    hasMinBookingSearchLength &&
    !!bookingsLookupQuery.pagination &&
    bookingsLookupQuery.pagination.totalCount > bookingsLookupQuery.pagination.pageSize;

  const handleBookingLookupPageChange = (
    getNextPageNumber: (currentPageNumber: number) => number
  ) => {
    setSelectedBookingId(null);
    setBookingLookupPageNumber((currentPageNumber) => getNextPageNumber(currentPageNumber));
  };

  const handleStartReservationQuickCreateFromBooking = () => {
    if (!selectedBookingId) {
      return;
    }

    onStartReservationQuickCreateFromBooking?.(selectedBookingId);
  };

  const handleStartReservationQuickCreateWithNewBooking = () => {
    onStartReservationQuickCreateWithNewBooking?.();
  };

  const renderApContractorRowLabel = (apContractor: BankDocumentApContractorLookup): string => {
    if (apContractor.nip) {
      return `${apContractor.name} (NIP: ${apContractor.nip})`;
    }

    return apContractor.name;
  };

  const renderInvoiceRow = (invoice: BankDocumentInvoiceLookup) => {
    const inflowSecondaryLine = buildSecondaryLine(
      invoice.applicantFullName,
      invoice.city,
      `pozostało ${invoice.remainingAmount.toFixed(2)} ${invoice.currency}`,
      formatDate(invoice.issueDate)
    );
    const outflowSecondaryLine = buildSecondaryLine(
      invoice.applicantFullName,
      invoice.city,
      `${invoice.totalAmount.toFixed(2)} ${invoice.currency}`,
      formatDate(invoice.issueDate)
    );

    if (!isOutflow) {
      const isAlreadyAdded = allocationRows.some(
        (allocation) => allocation.targetType === 'invoice' && allocation.targetId === invoice.id
      );

      return (
        <Stack
          key={invoice.id}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ py: 0.75 }}
        >
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {invoice.invoiceNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {inflowSecondaryLine}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            disabled={saving || isAlreadyAdded || !getNextAllocationAmount(invoice.remainingAmount)}
            onClick={() =>
              addAllocationRow(
                'invoice',
                invoice.id,
                invoice.remainingAmount,
                false,
                invoice.applicantFullName
              )
            }
          >
            {isAlreadyAdded ? 'Dodano' : 'Dodaj'}
          </Button>
        </Stack>
      );
    }

    return (
      <FormControlLabel
        key={invoice.id}
        value={String(invoice.id)}
        control={<Radio size="small" />}
        label={
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {invoice.invoiceNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {outflowSecondaryLine}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', mb: 0.5 }}
      />
    );
  };

  const resolveReservationApplicantLabel = (reservation: BankDocumentReservationLookup): string =>
    [reservation.applicantFirstName, reservation.applicantLastName].filter(Boolean).join(' ').trim() ||
    reservation.applicantName;

  const renderReservationRow = (reservation: BankDocumentReservationLookup) => {
    const applicantLabel = resolveReservationApplicantLabel(reservation);
    const payerLabel = normalizeDisplayLabel(reservation.invoiceBuyerDisplayName);
    const shouldShowPayerHint = Boolean(payerLabel) && !areDisplayLabelsEqual(payerLabel, applicantLabel);
    const bookingDateRange = `${formatDate(reservation.bookingDateFrom)}${
      reservation.bookingDateTo ? ` -> ${formatDate(reservation.bookingDateTo)}` : ''
    }`;
    const secondaryLine = buildSecondaryLine(
      reservation.offerName ?? '-',
      reservation.city,
      isOutflow
        ? `wpłacono ${reservation.paidAmount.toFixed(2)} ${reservation.currency}`
        : `pozostało ${reservation.remainingAmount.toFixed(2)} ${reservation.currency}`,
      bookingDateRange
    );
    const isAlreadyAdded = allocationRows.some(
      (allocation) => allocation.targetType === 'reservation' && allocation.targetId === reservation.id
    );

    return (
      <Stack
        key={reservation.id}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ py: 0.75 }}
      >
        <Box>
          <Typography variant="body2" fontWeight={500}>
            #{reservation.id}
            {applicantLabel ? ` - ${applicantLabel}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {secondaryLine}
          </Typography>
          {shouldShowPayerHint && (
            <Typography variant="caption" color="text.secondary">
              Płatnik: {payerLabel}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={
            saving ||
            isAlreadyAdded ||
            !getNextAllocationAmount(isOutflow ? undefined : reservation.remainingAmount, true)
          }
          onClick={() =>
            addAllocationRow(
              'reservation',
              reservation.id,
              isOutflow ? undefined : reservation.remainingAmount,
              true,
              applicantLabel
            )
          }
        >
          {isAlreadyAdded ? 'Dodano' : 'Dodaj'}
        </Button>
      </Stack>
    );
  };

  const renderBookingRow = (booking: BankDocumentBookingLookup) => {
    const bookingDateRange = `${formatDate(booking.bookingDateFrom)}${
      booking.bookingDateTo ? ` -> ${formatDate(booking.bookingDateTo)}` : ''
    }`;
    const secondaryLine = buildSecondaryLine(
      booking.offerName,
      booking.companyName ?? null,
      bookingDateRange,
      `${booking.numberOfParticipants} uczestników`
    );

    return (
      <FormControlLabel
        key={booking.id}
        value={String(booking.id)}
        control={<Radio size="small" />}
        label={
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Booking #{booking.id}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {secondaryLine}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', mb: 0.5 }}
      />
    );
  };

  const renderExpenseInvoiceRow = (expenseInvoice: BankDocumentExpenseInvoiceLookup) => (
    <FormControlLabel
      key={expenseInvoice.id}
      value={String(expenseInvoice.id)}
      control={<Radio size="small" />}
      label={
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {expenseInvoice.invoiceNumber}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {expenseInvoice.supplierName} &bull; {expenseInvoice.totalGross.toFixed(2)}{' '}
            {expenseInvoice.currency} &bull; {expenseInvoice.statusLabel}
          </Typography>
        </Box>
      }
      sx={{ alignItems: 'flex-start', mb: 0.5 }}
    />
  );

  const supplierApContractorOptions = useMemo(() => {
    if (!selectedSupplierApContractor) {
      return apContractors;
    }

    const hasSelectedOption = apContractors.some(
      (apContractor) => apContractor.id === selectedSupplierApContractor.id
    );

    return hasSelectedOption ? apContractors : [selectedSupplierApContractor, ...apContractors];
  }, [apContractors, selectedSupplierApContractor]);

  const visibleSupplierApContractorOptions = useMemo(() => {
    if (!hasMinSupplierApContractorSearchLength) {
      return [];
    }

    const normalizedFilter = normalizedSupplierApContractorInput.toLowerCase();

    return supplierApContractorOptions.filter((apContractor) => {
      const label = renderApContractorRowLabel(apContractor).toLowerCase();
      const name = apContractor.name.toLowerCase();
      const nip = apContractor.nip.toLowerCase();

      return (
        label.includes(normalizedFilter) ||
        name.includes(normalizedFilter) ||
        nip.includes(normalizedFilter)
      );
    });
  }, [
    hasMinSupplierApContractorSearchLength,
    normalizedSupplierApContractorInput,
    supplierApContractorOptions,
  ]);

  const isSupplierApContractorLoading =
    isSupplierApContractorDebouncePending || apContractorsLoading || apContractorsFetching;

  const renderAllocationEditor = () => (
    <>
      <Box
        data-testid="allocation-summary"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          py: 0.5,
        }}
      >
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={`${isOutflow ? 'Rozdzielono' : 'Przypisano'}: ${allocationAssignedAmount.toFixed(2)} ${entry?.currency ?? 'PLN'}`}
        />
        <Chip
          size="small"
          color={allocationRemainingAmount === 0 ? 'success' : 'warning'}
          variant="outlined"
          label={`${isOutflow ? 'Pozostało do rozdzielenia' : 'Pozostało'}: ${allocationRemainingAmount.toFixed(2)} ${entry?.currency ?? 'PLN'}`}
        />
        <Chip size="small" variant="outlined" label={pluralizeItems(allocationRows.length)} />
      </Box>

      {allocationRows.length > 0 && (
        <Stack spacing={1}>
          {allocationRows.map((allocation) => (
            <Stack
              key={allocation.localId}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {allocation.applicantName && (
                  <>
                    <Typography component="span" variant="body2" fontWeight={700}>
                      {allocation.applicantName}
                    </Typography>{' '}
                  </>
                )}
                <Typography component="span" variant="body2">
                  {formatAllocationTarget(allocation)}
                </Typography>
              </Box>
              <TextField
                size="small"
                label="Kwota"
                type="number"
                value={allocation.allocatedAmount ?? ''}
                inputProps={{ min: 0, step: '0.01' }}
                onChange={(event) => {
                  const value = event.target.value;
                  const numericValue = Number(value);
                  updateAllocationAmount(
                    allocation.localId,
                    value === '' ? null : Number.isFinite(numericValue) ? numericValue : null
                  );
                }}
                disabled={saving}
                sx={{ width: { xs: '100%', sm: 160 } }}
              />
              <Button
                size="small"
                color="warning"
                onClick={() => removeAllocationRow(allocation.localId)}
                disabled={saving}
              >
                Usuń
              </Button>
            </Stack>
          ))}
        </Stack>
      )}

      {allocationValidationMessage && <Alert severity="warning">{allocationValidationMessage}</Alert>}
      {!allocationValidationMessage && allocationRemainingAmount > 0 && isOutflow && (
        <Alert severity="info">
          Pozostało do rozdzielenia {allocationRemainingAmount.toFixed(2)} {entry?.currency ?? 'PLN'}.
          Szkic można zapisać z częściowym rozdzieleniem zwrotu, ale zaksięgowanie wymaga
          rozdzielenia całej kwoty.
        </Alert>
      )}
      {!allocationValidationMessage && allocationRemainingAmount > 0 && !isOutflow && (
        <Alert severity="info">
          Nieprzypisana reszta ({allocationRemainingAmount.toFixed(2)} {entry?.currency ?? 'PLN'})
          zostanie zaksięgowana jako nieprzypisana reszta.
        </Alert>
      )}
    </>
  );

  if (!entry) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Przypisanie pozycji #{entry.id}</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Tytuł: <strong>{entry.title || '(brak tytułu)'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Nadawca/Odbiorca: <strong>{entry.counterpartyName || '(brak danych)'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kwota:{' '}
              <strong>
                {entry.amount.toFixed(2)} {entry.currency}
              </strong>
            </Typography>
          </Stack>

          <FormControl>
            <FormLabel>Typ przypisania</FormLabel>
            <RadioGroup
              row
              value={assignmentType}
              onChange={(e) => handleAssignmentTypeChange(e.target.value as AssignmentType)}
            >
              <FormControlLabel value="invoice" control={<Radio />} label="Faktura" />
              <FormControlLabel value="reservation" control={<Radio />} label="Rezerwacja" />
              <FormControlLabel
                value="expenseInvoice"
                control={<Radio />}
                label="FV kosztowa"
                disabled={!isOutflow}
              />
              <FormControlLabel
                value="supplierPrepayment"
                control={<Radio />}
                label="Zaliczka dostawcy"
                disabled={!isOutflow}
              />
              <FormControlLabel value="none" control={<Radio />} label="Brak przypisania" />
            </RadioGroup>
          </FormControl>

          {selectedCompany && (
            <Alert severity="info">
              Pozycje lookup są filtrowane dla firmy: <strong>{selectedCompany.name}</strong>
            </Alert>
          )}

          {isOutflow && (
            <Alert severity="info">
              Dla kwot ujemnych dostępne jest przypisanie do <strong>Faktury</strong>,{' '}
              <strong>Rezerwacji</strong> (zwrot można rozdzielić na wiele rezerwacji),{' '}
              <strong>FV kosztowej</strong> oraz{' '}
              <strong>Zaliczki dostawcy</strong>.
            </Alert>
          )}

          {!isOutflow && (
            <Alert severity="info">
              Dla kwot dodatnich przypisanie do <strong>FV kosztowej</strong> i{' '}
              <strong>Zaliczki dostawcy</strong> jest wyłączone. Dostępne jest przypisanie do{' '}
              <strong>Faktury</strong> lub <strong>Rezerwacji</strong>.
            </Alert>
          )}

          {assignmentType === 'supplierPrepayment' && (
            <Stack spacing={1.5}>
              <Autocomplete
                size="small"
                fullWidth
                options={visibleSupplierApContractorOptions}
                value={selectedSupplierApContractor}
                inputValue={searchTerm}
                loading={isSupplierApContractorLoading}
                loadingText="Szukam AP kontrahentów..."
                getOptionLabel={renderApContractorRowLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => {
                  setSelectedSupplierApContractor(value);
                  setIsCreateApContractorOpen(false);
                  setCreateApContractorError(null);
                  setCreateApContractorSuccess(null);
                }}
                onInputChange={(_, value, reason) => {
                  setSearchTerm(value);
                  setCreateApContractorError(null);
                  setCreateApContractorSuccess(null);

                  if (reason === 'input' || reason === 'clear') {
                    setSelectedSupplierApContractor(null);
                  }
                }}
                disabled={saving || isCreateApContractorLoading}
                noOptionsText={
                  hasSupplierApContractorSearchTerm && !hasMinSupplierApContractorSearchLength
                    ? 'Wpisz minimum 2 znaki, aby wyszukać AP kontrahenta.'
                    : hasSupplierApContractorSearchTerm
                      ? `Brak AP kontrahentów dla "${normalizedSupplierApContractorInput}".`
                      : 'Wpisz nazwę lub NIP AP kontrahenta.'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="AP kontrahent"
                    placeholder="Wyszukaj po nazwie lub NIP"
                    autoFocus
                  />
                )}
              />

              <Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsCreateApContractorOpen((current) => !current);
                    setCreateApContractorError(null);
                    setCreateApContractorSuccess(null);
                  }}
                  disabled={saving || isCreateApContractorLoading}
                >
                  {isCreateApContractorOpen
                    ? 'Ukryj formularz nowego AP kontrahenta'
                    : 'Dodaj nowego AP kontrahenta'}
                </Button>
              </Box>

              {isCreateApContractorOpen && (
                <Stack spacing={1.5}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Nazwa nowego AP kontrahenta"
                    value={newApContractorName}
                    onChange={(event) => setNewApContractorName(event.target.value)}
                    disabled={saving || isCreateApContractorLoading}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="NIP nowego AP kontrahenta"
                    value={newApContractorNip}
                    onChange={(event) => setNewApContractorNip(event.target.value)}
                    inputProps={{ maxLength: 20 }}
                    disabled={saving || isCreateApContractorLoading}
                  />
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={handleCreateApContractor}
                      disabled={saving || isCreateApContractorLoading}
                    >
                      {isCreateApContractorLoading ? 'Tworzenie…' : 'Utwórz i wybierz'}
                    </Button>
                  </Box>
                </Stack>
              )}

              {createApContractorSuccess && (
                <Alert severity="success">{createApContractorSuccess}</Alert>
              )}

              {createApContractorError && <Alert severity="error">{createApContractorError}</Alert>}

              <Alert severity="warning">
                Operacja utworzy zaliczkę dla wybranego AP kontrahenta. Bez wskazania AP kontrahenta
                zapis jest blokowany.
              </Alert>
            </Stack>
          )}

          {assignmentType === 'invoice' && (
            <Stack spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="Szukaj faktury (nr, nazwisko, e-mail…)"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setLookupPageNumber(1);
                  setSelectedInvoiceId(null);
                }}
                disabled={saving}
                autoFocus
              />
              {!isOutflow && renderAllocationEditor()}
              {hasSearchTerm && shouldSkipLookupBySearchLength && (
                <Typography variant="caption" color="text.secondary">
                  Wpisz minimum 2 znaki, aby wyszukać fakturę.
                </Typography>
              )}
              {invoicesFetching && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!invoicesFetching &&
                !shouldSkipLookupBySearchLength &&
                invoices.length === 0 &&
                hasSearchTerm && (
                  <Typography variant="caption" color="text.secondary">
                    Brak wyników dla „{normalizedSearchTerm}".
                  </Typography>
                )}
              {!invoicesFetching && invoices.length > 0 && (
                <FormControl>
                  <RadioGroup
                    value={selectedInvoiceId !== null ? String(selectedInvoiceId) : ''}
                    onChange={(e) => setSelectedInvoiceId(parseInt(e.target.value, 10))}
                  >
                    {invoices.map(renderInvoiceRow)}
                  </RadioGroup>
                </FormControl>
              )}
              {showLookupPaginationControls && (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                  <Typography variant="caption" color="text.secondary">
                    Strona {lookupPageNumber}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleLookupPageChange((currentPageNumber) =>
                        Math.max(1, currentPageNumber - 1)
                      )
                    }
                    disabled={saving || activeLookupIsFetching || !canGoToPreviousLookupPage}
                  >
                    Poprzednia
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleLookupPageChange((currentPageNumber) => currentPageNumber + 1)
                    }
                    disabled={saving || activeLookupIsFetching || !canGoToNextLookupPage}
                  >
                    Następna
                  </Button>
                </Stack>
              )}
            </Stack>
          )}

          {assignmentType === 'reservation' && (
            <Stack spacing={1}>
              {shouldShowReservationQuickCreateCta && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Szybkie tworzenie z dokumentu bankowego</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      size="small"
                      variant={isBookingLookupVisible ? 'contained' : 'outlined'}
                      onClick={() => {
                        setIsBookingLookupVisible(true);
                        setSelectedBookingId(null);
                        setBookingLookupPageNumber(1);
                      }}
                      disabled={
                        saving ||
                        !isReservationQuickCreateEntryEligible ||
                        !onStartReservationQuickCreateFromBooking
                      }
                    >
                      Mam istniejący booking
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleStartReservationQuickCreateWithNewBooking}
                      disabled={
                        saving ||
                        !isReservationQuickCreateEntryEligible ||
                        !onStartReservationQuickCreateWithNewBooking
                      }
                    >
                      Utwórz booking i reservation
                    </Button>
                  </Stack>

                  {reservationQuickCreateDisabledReason && (
                    <Alert severity="info">{reservationQuickCreateDisabledReason}</Alert>
                  )}

                  {isBookingLookupVisible && (
                    <Stack spacing={1}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Szukaj bookingu (oferta, firma, data...)"
                        value={bookingSearchTerm}
                        onChange={(event) => {
                          setBookingSearchTerm(event.target.value);
                          setBookingLookupPageNumber(1);
                          setSelectedBookingId(null);
                        }}
                        disabled={saving || !isReservationQuickCreateEntryEligible}
                      />

                      {hasBookingSearchTerm && shouldSkipBookingLookupBySearchLength && (
                        <Typography variant="caption" color="text.secondary">
                          Wpisz minimum 2 znaki, aby wyszukać booking.
                        </Typography>
                      )}

                      {bookingsFetching && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}

                      {!bookingsFetching &&
                        !shouldSkipBookingLookupBySearchLength &&
                        bookings.length === 0 &&
                        hasBookingSearchTerm && (
                          <Typography variant="caption" color="text.secondary">
                            Brak bookingów dla „{normalizedBookingSearchTerm}".
                          </Typography>
                        )}

                      {!bookingsFetching && bookings.length > 0 && (
                        <FormControl>
                          <RadioGroup
                            value={selectedBookingId !== null ? String(selectedBookingId) : ''}
                            onChange={(event) => setSelectedBookingId(parseInt(event.target.value, 10))}
                          >
                            {bookings.map(renderBookingRow)}
                          </RadioGroup>
                        </FormControl>
                      )}

                      {showBookingLookupPaginationControls && (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                          <Typography variant="caption" color="text.secondary">
                            Strona {bookingLookupPageNumber}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleBookingLookupPageChange((currentPageNumber) =>
                                Math.max(1, currentPageNumber - 1)
                              )
                            }
                            disabled={
                              saving || bookingsFetching || !canGoToPreviousBookingLookupPage
                            }
                          >
                            Poprzednia
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleBookingLookupPageChange(
                                (currentPageNumber) => currentPageNumber + 1
                              )
                            }
                            disabled={saving || bookingsFetching || !canGoToNextBookingLookupPage}
                          >
                            Następna
                          </Button>
                        </Stack>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleStartReservationQuickCreateFromBooking}
                          disabled={saving || !selectedBookingId}
                        >
                          Przejdź do tworzenia rezerwacji
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              )}

              <TextField
                size="small"
                fullWidth
                label="Szukaj rezerwacji (nazwisko, płatnik, oferta, data…)"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setLookupPageNumber(1);
                  setSelectedReservationId(null);
                }}
                disabled={saving}
                autoFocus
              />
              {renderAllocationEditor()}
              {hasSearchTerm && shouldSkipLookupBySearchLength && (
                <Typography variant="caption" color="text.secondary">
                  Wpisz minimum 2 znaki, aby wyszukać rezerwację.
                </Typography>
              )}
              {reservationsFetching && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!reservationsFetching &&
                !shouldSkipLookupBySearchLength &&
                reservations.length === 0 &&
                hasSearchTerm && (
                  <Typography variant="caption" color="text.secondary">
                    Brak wyników dla „{normalizedSearchTerm}".
                  </Typography>
                )}
              {!reservationsFetching && reservations.length > 0 && (
                <FormControl>
                  <RadioGroup
                    value={selectedReservationId !== null ? String(selectedReservationId) : ''}
                    onChange={(e) => setSelectedReservationId(parseInt(e.target.value, 10))}
                  >
                    {reservations.map(renderReservationRow)}
                  </RadioGroup>
                </FormControl>
              )}
              {showLookupPaginationControls && (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                  <Typography variant="caption" color="text.secondary">
                    Strona {lookupPageNumber}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleLookupPageChange((currentPageNumber) =>
                        Math.max(1, currentPageNumber - 1)
                      )
                    }
                    disabled={saving || activeLookupIsFetching || !canGoToPreviousLookupPage}
                  >
                    Poprzednia
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleLookupPageChange((currentPageNumber) => currentPageNumber + 1)
                    }
                    disabled={saving || activeLookupIsFetching || !canGoToNextLookupPage}
                  >
                    Następna
                  </Button>
                </Stack>
              )}
            </Stack>
          )}

          {assignmentType === 'expenseInvoice' && (
            <Stack spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="Szukaj FV kosztowej (nr, dostawca…)"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setLookupPageNumber(1);
                  setSelectedExpenseInvoiceId(null);
                }}
                disabled={saving}
                autoFocus
              />
              {hasSearchTerm && shouldSkipLookupBySearchLength && (
                <Typography variant="caption" color="text.secondary">
                  Wpisz minimum 2 znaki, aby wyszukać FV kosztową.
                </Typography>
              )}
              {expenseInvoicesFetching && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!expenseInvoicesFetching &&
                !shouldSkipLookupBySearchLength &&
                expenseInvoices.length === 0 &&
                hasSearchTerm && (
                  <Typography variant="caption" color="text.secondary">
                    Brak wyników dla „{normalizedSearchTerm}".
                  </Typography>
                )}
              {!expenseInvoicesFetching && expenseInvoices.length > 0 && (
                <FormControl>
                  <RadioGroup
                    value={
                      selectedExpenseInvoiceId !== null ? String(selectedExpenseInvoiceId) : ''
                    }
                    onChange={(e) => setSelectedExpenseInvoiceId(parseInt(e.target.value, 10))}
                  >
                    {expenseInvoices.map(renderExpenseInvoiceRow)}
                  </RadioGroup>
                </FormControl>
              )}
              {showLookupPaginationControls && (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                  <Typography variant="caption" color="text.secondary">
                    Strona {lookupPageNumber}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleLookupPageChange((currentPageNumber) =>
                        Math.max(1, currentPageNumber - 1)
                      )
                    }
                    disabled={saving || activeLookupIsFetching || !canGoToPreviousLookupPage}
                  >
                    Poprzednia
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleLookupPageChange((currentPageNumber) => currentPageNumber + 1)
                    }
                    disabled={saving || activeLookupIsFetching || !canGoToNextLookupPage}
                  >
                    Następna
                  </Button>
                </Stack>
              )}
            </Stack>
          )}

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Anuluj
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaveDisabled}>
          {saving ? 'Zapisywanie…' : 'Zapisz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignEntryDialog;
